import { datadogLogs } from '@datadog/browser-logs';

export type PixelEvent =
  | 'signup'
  | 'donation'
  | 'purchase'
  | 'universalPixel'
  | 'questions'
  | 'donationSuccess';

type Donation = { amount: number; unit: 'cents' | 'dollars' };

// Also excluded from broadcast rewrites in public/meta-pixel.js — keep both in sync.
export const OATH_PIXEL_ID = '1398701991039055';
// Injected by HubSpot's own loader, so a rotation on their side fails silently here.
export const HUBSPOT_PIXEL_ID = '1003124694353851';

// Partner pixels are deliberately absent — usePartnerTracking scopes those itself.
const OATH_META_PIXEL_IDS = [OATH_PIXEL_ID, HUBSPOT_PIXEL_ID];

const TRACKING_PIXEL_GTAG: Partial<Record<PixelEvent, string>> = {
  signup: 'AW-11462753602/eGcCCInZ54cZEML67tkq',
  donation: 'AW-16513322465/PMHRCNqslaIZEOGblcI9',
};

const TRACKING_PIXEL_FBQ: Partial<Record<PixelEvent, string>> = {
  signup: 'CompleteRegistration',
  donation: 'InitiateCheckout',
  purchase: 'Purchase',
};

const TRACKING_PIXEL_SAQ: Partial<Record<PixelEvent, string>> = {
  universalPixel: 'UniversalPixel',
};

const TRACKING_PIXEL_TWQ: Partial<Record<PixelEvent, string>> = {
  questions: 'tw-oonml-ooqly',
  donationSuccess: 'tw-oonml-ooqm4',
};

const toDollars = ({ amount, unit }: Donation) => (unit === 'cents' ? amount / 100 : amount);

// The shim in public/meta-pixel.js can't reach Datadog; it parks failures for us.
let shimErrorReported = false;
const reportShimError = () => {
  const shimError = window.__oathPixelShim?.error;
  if (!shimError || shimErrorReported) return;
  shimErrorReported = true;
  datadogLogs.logger.warn('Meta pixel broadcast shim degraded', { shim_error: shimError });
};

/**
 * Subscribes to shim failures for the rest of the page's life. trackPixel's own call is
 * not enough on its own: a visitor who lands on a partner page and bounces never fires a
 * pixel event, and that is exactly the session where a degraded shim has silently gone
 * back to cross-firing Oath's events at the partner. Also drains an error parked before
 * this ran, since the shim is live from the first script tag.
 */
export const watchShimErrors = () => {
  const shim = window.__oathPixelShim;
  if (shim) shim.onError = reportShimError;
  reportShimError();
};

/**
 * Guards the mount-effect Purchase against a remount (or StrictMode's double-invoke)
 * double-counting; the conversion id doubles as Meta's eventID. No id means fire
 * unguarded — an overcount beats dropping a conversion.
 */
export const trackPurchaseOnce = (conversionId: string | undefined, donation: Donation) => {
  if (conversionId) {
    try {
      const firedKey = `oath_purchase_fired:${conversionId}`;
      if (sessionStorage.getItem(firedKey)) return;
      sessionStorage.setItem(firedKey, '1');
    } catch {
      // Throws in some privacy modes; fall through and fire.
    }
  }
  trackPixel('purchase', donation, conversionId);
};

export const trackPixel = (event: PixelEvent, donation?: Donation, eventId?: string) => {
  try {
    reportShimError();
    const gtagEvent = TRACKING_PIXEL_GTAG[event];
    if (gtagEvent && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: gtagEvent });
    }

    const fbq = window.fbq;
    const fbqEvent = TRACKING_PIXEL_FBQ[event];
    if (fbqEvent && typeof fbq === 'function') {
      // trackSingle, not track: a bare track reaches every pixel in the shared singleton.
      const params = donation ? { value: toDollars(donation), currency: 'USD' } : undefined;
      // eventID dedupes against a server-side (Conversions API) send only — Meta keeps
      // both of two browser sends, so call sites must still guard.
      const options = eventId ? { eventID: eventId } : undefined;
      OATH_META_PIXEL_IDS.forEach(pixelId => {
        if (options) fbq('trackSingle', pixelId, fbqEvent, params ?? {}, options);
        else if (params) fbq('trackSingle', pixelId, fbqEvent, params);
        else fbq('trackSingle', pixelId, fbqEvent);
      });
    }

    const saqEvent = TRACKING_PIXEL_SAQ[event];
    if (saqEvent && typeof window.saq === 'function') {
      window.saq('event', saqEvent);
    }

    const twqEvent = TRACKING_PIXEL_TWQ[event];
    if (twqEvent && typeof window.twq === 'function') {
      window.twq('event', twqEvent, {});
    }
  } catch (error) {
    datadogLogs.logger.warn(
      'Error in trackPixel',
      { pixel_event: event },
      error instanceof Error ? error : new Error(String(error))
    );
  }
};
